import ListViewModel from "viewModels/listViewModel";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Router } from "@frui.ts/screens";
import InternalReportsRepository from "repositories/internalReportsRepository";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import { watchBusy } from "utils/busyWatcher";
import { allowToDownload } from "../rights";
import { Modules } from "config";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import DateRangeRequest, { OptionalDateRangeSameDayAllowedValidationRules } from "entities/dateRangeRequest";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import InternalReportResponse from "entities/internalReportResponse";
import type EnforcementReportDetailViewModel from "../enforcementReportDetailViewModel";
import { attachAutomaticDirtyWatcher } from "@frui.ts/dirtycheck";

const navigationName = "mobile-control";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class MobileControlReportsListViewModel extends ListViewModel<
  InternalReportResponse,
  EnforcementReportDetailViewModel,
  ScreenBase,
  any
> {
  readonly modelName = "internalReportResponse";
  @observable items: InternalReportResponse[] = [];
  @observable fileResponse: any = undefined;
  @observable request = new DateRangeRequest();

  readonly idProperty = "id";

  @computed
  get name() {
    return this.translate("name");
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof EnforcementReportDetailViewModel.Factory>,
    public repository: InternalReportsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService
  ) {
    super();
    this.navigationName = navigationName;

    attachAutomaticValidator(this.request, OptionalDateRangeSameDayAllowedValidationRules);
    attachAutomaticDirtyWatcher(this.request, true);
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.enforcement, `enforcementReports.mobileControl.${code}`, size);
  }

  @action.bound
  @watchBusy
  async onFormSubmit() {
    if (!validate(this.request)) {
      return;
    }
    await this.onGenerateReportClick(this.request.from, this.request.to);
  }

  @action.bound
  onFormReset() {
    this.request.from = undefined;
    this.request.to = undefined;
  }

  @action.bound
  @watchBusy
  async onGenerateReportClick(fromDate?: Date, toDate?: Date) {
    try {
      const result = await this.repository.generateNewMobileControlReport(fromDate, toDate);

      if (result.reportRequestId) {
        const fileName = `mobile_control_report_${new Date().toISOString()}.xlsx`;

        await this.download({ id: result.reportRequestId, fileName: fileName, createdBy: "", status: result.reportStatus });
        this.notificationService.add({ title: this.translate("alreadyGeneratedMessage"), type: NotificationType.SUCCESS });
      } else {
        this.notificationService.add({ title: this.translate("generatingMessage"), type: NotificationType.SUCCESS });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @action.bound
  @watchBusy
  async onDownload(item: InternalReportResponse) {
    await this.download(item);
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  @computed
  get allowWrite() {
    return (
      this.securityService.allowTo("enforcement.reports", "write") || this.securityService.allowToEntity(this.modelName, "write")
    );
  }

  protected async fetchData() {
    const reports = await this.repository.postMobileControlReports();

    reports.items = reports.items.sort((a, b) => {
      if (!a.processedAt || !b.processedAt) {
        return -1;
      }
      return b.processedAt.getTime() - a.processedAt.getTime();
    });

    return reports;
  }

  async download(item: InternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.fileName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
